/**
 * CENTRALIZED COLUMN DEFINITIONS
 * This file contains the default column configurations and field mappings for the application.
 */

// ============================================================================
// 1. CENTRALIZED FIELD KEYS & MAPPINGS
// ============================================================================
import type { InspectorColumnConfig } from '../components/inspector/types'

export const FIELD_KEYS = {
    TIMESTAMP: ['timestamp', 'time', 'timestampIST'],
    // CE Fields
    CE_LTP: ['callPrice', 'ceLtp', 'ce_ltp', 'CE_LTP', 'lastPrice'],
    CE_OI: ['callOpenInterest', 'ceOI', 'ce_oi', 'CE_OI'],
    CE_VOL: ['callVolume', 'ceVol', 'ce_vol', 'CE_Volume'],
    CE_IV: ['callImpliedVolatility', 'ceIv', 'ce_iv', 'CE_IV'],
    CE_OI_CHG: ['callOpenInterestChange', 'ceOiChange', 'ce_oi_change', 'CE_OI_Change'],
    CE_PRICE_CHG: ['callChangePercent', 'ceChangePercent', 'ceChange', 'ce_change_p', 'change_p', 'pChange'],

    // PE Fields
    PE_LTP: ['putPrice', 'peLtp', 'pe_ltp', 'PE_LTP', 'lastPrice'],
    PE_OI: ['putOpenInterest', 'peOI', 'pe_oi', 'PE_OI'],
    PE_VOL: ['putVolume', 'peVol', 'pe_vol', 'PE_Volume'],
    PE_IV: ['putImpliedVolatility', 'peIv', 'pe_iv', 'PE_IV'],
    PE_OI_CHG: ['putOpenInterestChange', 'peOiChange', 'pe_oi_change', 'PE_OI_Change'],
    PE_PRICE_CHG: ['putChangePercent', 'peChangePercent', 'peChange', 'pe_change_p', 'change_p', 'pChange'],

    // Greeks (CE)
    CE_DELTA: ['callDelta', 'ceDelta'],
    CE_THETA: ['callTheta', 'ceTheta'],
    CE_GAMMA: ['callGamma', 'ceGamma'],
    CE_VEGA: ['callVega', 'ceVega'],

    // Greeks (PE)
    PE_DELTA: ['putDelta', 'peDelta'],
    PE_THETA: ['putTheta', 'peTheta'],
    PE_GAMMA: ['putGamma', 'peGamma'],
    PE_VEGA: ['putVega', 'peVega'],

    // Absolute Change
    CE_CHANGE: ['callChange', 'ceChangeAbs', 'ce_change'],
    PE_CHANGE: ['putChange', 'peChangeAbs', 'pe_change'],

    // Bid/Ask (CE)
    CE_BID_QTY: ['callBidQty', 'ceBidQty'],
    CE_BID_PRICE: ['callBidPrice', 'ceBidPrice'],
    CE_ASK_PRICE: ['callAskPrice', 'ceAskPrice'],
    CE_ASK_QTY: ['callAskQty', 'ceAskQty'],

    // Bid/Ask (PE)
    PE_BID_QTY: ['putBidQty', 'peBidQty'],
    PE_BID_PRICE: ['putBidPrice', 'peBidPrice'],
    PE_ASK_PRICE: ['putAskPrice', 'peAskPrice'],
    PE_ASK_QTY: ['putAskQty', 'peAskQty'],

    // Base Fields
    STRIKE: ['strikePrice', 'strike', 'strike_price'],
    EXPIRY: ['expiryDate', 'expiry'],

    // Strategy Fields
    STRATEGY_NAME: ['strategy', 'name', 'strategyName', 'id'],
    STRATEGY_TYPE: ['type', 'strategyType'],
    NET_PREMIUM: ['netPremium', 'totalPremium', 'net_premium'],
    MAX_PROFIT: ['maxProfit', 'max_profit'],
    MAX_LOSS: ['maxLoss', 'max_loss'],
    BREAKEVEN_LOWER: ['breakevenLower', 'be_lower', 'lower_be'],
    BREAKEVEN_UPPER: ['breakevenUpper', 'be_upper', 'upper_be'],
    POP: ['pop', 'probOfProfit'],
    RISK_REWARD: ['riskReward', 'rr', 'risk_reward'],

    // Analysis
    RESISTANCE: ['resistanceStrike', 'resistance_strike', 'callResistance'],
    RESISTANCE_2: ['resistanceStrike2', 'resistance_strike_2', 'resistance_strike2'],
    SUPPORT: ['supportStrike', 'support_strike', 'putSupport'],
    SUPPORT_2: ['supportStrike2', 'support_strike_2', 'support_strike2'],

    // Counts (Re-added)
    CE_COUNT: ['ceCount', 'ce_count'],
    PE_COUNT: ['peCount', 'pe_count'],
    FINAL_COUNT: ['finalCount', 'totalCount', 'total_count', 'final_count'],
    ATM: ['atm', 'atmStrike', 'atm_strike'],

    // Metrics
    VARIANCE: ['finalVariance', 'variance', 'oiVariance'],
    SHIFT: ['shift', 'shiftValue'],
    SHIFT_OLD: ['shiftOld', 'shift_old'],

    // Dashboard/Variance Extra
    MAX_OI_DIFF: ['maxDiff', 'max_diff', 'maxOiDiff'],
    MIN_OI_DIFF: ['minDiff', 'min_diff', 'minOiDiff'],
    MAX_STRIKE: ['maxStrike', 'max_strike'],
    MIN_STRIKE: ['minStrike', 'min_strike'],
    SPOT_PRICE: ['spotPrice', 'spot_price', 'underlyingPrice', 'lastPrice'],
    ANALYSIS: ['analysis', 'analysis_mode'],
    RESISTANCE_VALUE: ['resistanceValue', 'resistance_value'],
    SUPPORT_VALUE: ['supportValue', 'support_value'],
    CE_OTM: ['ceOtmStrike', 'ce_otm_strike', 'ceOtm', 'ce_otm'],
    PE_OTM: ['peOtmStrike', 'pe_otm_strike', 'peOtm', 'pe_otm'],
} as const

/**
 * Helper to get first valid value from an object using FIELD_KEYS
 */
export function getFieldValue(obj: any, keys: readonly string[]): any {
    if (!obj) return undefined
    for (const key of keys) {
        if (obj[key] !== undefined) return obj[key]
    }
    return undefined
}

// ============================================================================
// 2. COLUMN DEFINITIONS (UI CONSTANTS)
// ============================================================================
export const COLUMN_IDS = {
    TIME: 'time',
    RANK: 'rank',

    CE_LTP: 'ce_ltp',
    PE_LTP: 'pe_ltp',

    RESISTANCE_VALUE: 'resistance_value',
    SUPPORT_VALUE: 'support_value',

    RES_STRIKE: 'resistance_strike',
    RES_STRIKE_2: 'resistance_strike2',
    SUP_STRIKE: 'support_strike',
    SUP_STRIKE_2: 'support_strike2',

    CE_COUNT: 'ce_count',
    PE_COUNT: 'pe_count',
    FINAL_COUNT: 'final_count',

    MAX_OI_DIFF: 'max_oi_diff',
    MIN_OI_DIFF: 'min_oi_diff',
    MAX_STRIKE: 'max_strike',
    MIN_STRIKE: 'min_strike',
    VARIANCE: 'variance',

    SHIFT: 'shift',
    SHIFT_OLD: 'shift_old',
    ATM: 'atm_strike',
    CE_OTM: 'ce_otm',
    PE_OTM: 'pe_otm',
    COMBINED_OI: 'combined_oi',
    COMBINED_OI_CHANGE: 'combined_oi_change',
    COMBINED_OI_CHANGE_P: 'combined_oi_change_p',
    COMBINED_VOL: 'combined_volume',
    COMBINED_VOL_CHANGE: 'combined_vol_change',
    COMBINED_VOL_CHANGE_P: 'combined_vol_change_p',
} as const

// ============================================================================
// 3. TABLE SPECIFIC CONFIGURATIONS
// ============================================================================

export const CROSSOVER_COLUMNS = [
    { id: COLUMN_IDS.TIME, label: 'Time', visible: true },
    { id: COLUMN_IDS.RANK, label: 'Rank', visible: true },
    { id: COLUMN_IDS.CE_LTP, label: 'CE LTP', visible: true },
    { id: COLUMN_IDS.RESISTANCE_VALUE, label: 'OI/Volume', visible: true },
    { id: COLUMN_IDS.RES_STRIKE, label: 'Resistance', visible: true },
    { id: COLUMN_IDS.RES_STRIKE_2, label: 'Resistance 2', visible: true },
    { id: COLUMN_IDS.SUP_STRIKE, label: 'Support', visible: true },
    { id: COLUMN_IDS.SUP_STRIKE_2, label: 'Support 2', visible: true },
    { id: COLUMN_IDS.SUPPORT_VALUE, label: 'OI/Volume', visible: true },
    { id: COLUMN_IDS.PE_LTP, label: 'PE LTP', visible: true },
    { id: COLUMN_IDS.CE_COUNT, label: 'CE Count', visible: true },
    { id: COLUMN_IDS.PE_COUNT, label: 'PE Count', visible: true },
    { id: COLUMN_IDS.FINAL_COUNT, label: 'Final Count', visible: true },
    { id: COLUMN_IDS.MAX_OI_DIFF, label: 'Max OI % Diff', visible: true },
    { id: COLUMN_IDS.MIN_OI_DIFF, label: 'Min OI % Diff', visible: true },
    { id: COLUMN_IDS.VARIANCE, label: 'OI% Variance', visible: true },
    { id: COLUMN_IDS.SHIFT, label: 'Shift', visible: true },
    { id: COLUMN_IDS.SHIFT_OLD, label: 'Shift Old', visible: true },
    { id: COLUMN_IDS.ATM, label: 'ATM Strike', visible: true },
    { id: COLUMN_IDS.CE_OTM, label: 'CE OTM Strike', visible: true },
    { id: COLUMN_IDS.PE_OTM, label: 'PE OTM Strike', visible: true },
]

export const VARIANCE_COLUMNS = [
    { id: COLUMN_IDS.TIME, label: 'Time', visible: true },
    { id: COLUMN_IDS.MAX_OI_DIFF, label: 'Max OI', visible: true },
    { id: COLUMN_IDS.MAX_STRIKE, label: 'MAX STRIKE', visible: true },
    { id: COLUMN_IDS.MIN_STRIKE, label: 'MIN STRIKE', visible: true },
    { id: COLUMN_IDS.MIN_OI_DIFF, label: 'Min OI', visible: true },
    { id: COLUMN_IDS.CE_COUNT, label: 'CE Count', visible: true },
    { id: COLUMN_IDS.PE_COUNT, label: 'PE Count', visible: true },
    { id: COLUMN_IDS.FINAL_COUNT, label: 'Final Count', visible: true },
    { id: COLUMN_IDS.VARIANCE, label: 'OI% Variance', visible: true },
    { id: COLUMN_IDS.SHIFT, label: 'Shift', visible: true },
    { id: COLUMN_IDS.SHIFT_OLD, label: 'Shift Old', visible: true },
    { id: 'signal', label: 'Signal', visible: true },
    { id: COLUMN_IDS.ATM, label: 'ATM', visible: true },
    { id: COLUMN_IDS.CE_OTM, label: 'CE OTM Strike', visible: true },
    { id: COLUMN_IDS.PE_OTM, label: 'PE OTM Strike', visible: true },
]

export const DASHBOARD_HISTORY_COLUMNS = [
    { id: COLUMN_IDS.TIME, label: 'Time Log', visible: true },
    { id: COLUMN_IDS.SHIFT, label: 'Shift', visible: true },
    { id: 'res_strike', label: 'Resistance', visible: true },
    { id: 'res_ltp', label: 'R1 LTP', visible: true },
    { id: 'res2_strike', label: 'Resistance 2', visible: true },
    { id: 'res2_ltp', label: 'R2 LTP', visible: true },
    { id: 'sup_strike', label: 'Support', visible: true },
    { id: 'sup_ltp', label: 'Sup LTP', visible: true },
]

export const INSPECTOR_COLUMNS = [
    {
        id: 'iv',
        label: 'IV',
        ceField: FIELD_KEYS.CE_IV,
        peField: FIELD_KEYS.PE_IV,
        format: 'decimal',
        visible: true,
    },
    {
        id: 'oi_change',
        label: 'OI Chg (Lakh)',
        ceField: FIELD_KEYS.CE_OI_CHG,
        peField: FIELD_KEYS.PE_OI_CHG,
        format: 'lakh',
        visible: true,
    },
    {
        id: 'change_p',
        label: 'Price Chg %',
        ceField: FIELD_KEYS.CE_PRICE_CHG,
        peField: FIELD_KEYS.PE_PRICE_CHG,
        format: 'percent',
        visible: true,
    },
    {
        id: 'ltp',
        label: 'Price',
        ceField: FIELD_KEYS.CE_LTP,
        peField: FIELD_KEYS.PE_LTP,
        format: 'decimal',
        visible: true,
    },
    {
        id: 'oi',
        label: 'OI (Lakh)',
        ceField: FIELD_KEYS.CE_OI,
        peField: FIELD_KEYS.PE_OI,
        format: 'lakh',
        visible: true,
    },
    {
        id: 'volume',
        label: 'Volume (Cr)',
        ceField: FIELD_KEYS.CE_VOL,
        peField: FIELD_KEYS.PE_VOL,
        format: 'crore',
        visible: true,
    },
    {
        id: 'cum_price',
        label: 'Total Prem',
        ceField: FIELD_KEYS.CE_LTP,
        peField: FIELD_KEYS.PE_LTP,
        format: 'cumulative',
        visible: true,
    },
    // Combined Metrics (Same Strike)
    {
        id: 'ce_combined_oi',
        label: 'CE Comb OI',
        ceField: ['ceCombinedOI'],
        format: 'lakh',
        visible: true,
    },
    {
        id: 'ce_combined_vol',
        label: 'CE Comb Vol',
        ceField: ['ceCombinedVol'],
        format: 'crore',
        visible: true,
    },
    {
        id: 'pe_combined_oi',
        label: 'PE Comb OI',
        peField: ['peCombinedOI'],
        format: 'lakh',
        visible: true,
    },
    {
        id: 'pe_combined_vol',
        label: 'PE Comb Vol',
        peField: ['peCombinedVol'],
        format: 'crore',
        visible: true,
    },

    // Cross-Strike Differences
    {
        id: 'oi_diff',
        label: 'OI Diff (Lakh)',
        ceField: ['crossOIDiff'],
        peField: ['crossOIDiff'],
        format: 'lakh',
        visible: true,
    },
    {
        id: 'vol_diff',
        label: 'Vol Diff (Cr)',
        ceField: ['crossVolDiff'],
        peField: ['crossVolDiff'],
        format: 'crore',
        visible: true,
    },
] as InspectorColumnConfig[]

export const DASHBOARD_RANKING_COLUMNS = [
    { id: 'oi', label: 'OI (Lakhs)', visible: true },
    { id: 'volume', label: 'Volume (Cr)', visible: true },
    { id: 'iv', label: 'IV', visible: true },
    { id: 'ltp', label: 'LTP', visible: true },
    { id: 'change_p', label: 'Price Chg %', visible: true },
]

export const MULTI_STRATEGY_COLUMNS = [
    { id: 'strategy', label: 'Strategy', visible: true },
    { id: 'net_premium', label: 'Net Premium', visible: true },
    { id: 'max_profit', label: 'Max Profit', visible: true },
    { id: 'max_loss', label: 'Max Loss', visible: true },
    { id: 'breakeven', label: 'Breakeven Range', visible: true },
    { id: 'pop', label: 'PoP %', visible: true },
    { id: 'rr', label: 'R:R', visible: true },
    { id: 'action', label: 'Action', visible: true },
]

export const CONFIG_COLUMNS = {
    CROSSOVER: CROSSOVER_COLUMNS,
    VARIANCE: VARIANCE_COLUMNS,
    DASHBOARD: DASHBOARD_HISTORY_COLUMNS,
    INSPECTOR: INSPECTOR_COLUMNS,
    DASHBOARD_RANKING: DASHBOARD_RANKING_COLUMNS,
    MULTI_STRATEGY: MULTI_STRATEGY_COLUMNS,
}

// --- HELPER EXPORTS ---
export const getInspectorColumns = () => INSPECTOR_COLUMNS
export const getColumnLabel = (id: string, columns: any[]) => columns.find((c) => c.id === id)?.label || id
